<template>
  <b-modal
      :id="modalId"
      :title="title"
      :ok-disabled="selectedFiles.length === 0"
      size="md"
      @hidden="handleCancel"
      @cancel="handleCancel"
      @ok="handleConfirm"
      ok-title="Confirmar"
      cancel-title="Cancelar"
  >
    <div class="file-upload-container">
      <!-- Drag & Drop Area -->
      <div
          class="drop-zone"
          :class="{ 'drag-over': isDragOver, 'files-active' : selectedFiles.length > 0 }"
          @drop="handleDrop"
          @dragover.prevent="isDragOver = true"
          @dragleave="isDragOver = false"
          @dragenter.prevent
          @click="handleDropzoneClick"
      >
        <div class="drop-zone-content">
          <template v-if="selectedFiles.length === 0">
            <i class="fas fa-cloud-upload-alt fa-2x mb-3 icon-upload"></i>
            <p>Toque para selecionar arquivos ou arraste aqui</p>
          </template>
          <template v-else>
            <!-- File List -->
            <div v-if="selectedFiles.length" class="file-list">
              <div v-for="(file, index) in selectedFiles" :key="index" class="file-item">
                <div class="file-info">
                  <i class="fas fa-file mr-2"></i>
                  <span>{{ file.name }}</span>
                  <small class="text-muted ml-2">({{ formatFileSize(file.size) }})</small>
                </div>
                <b-button size="sm" variant="outline-danger" class="remove-file-btn" @click="removeFile(index)">
                  <i class="fas fa-times"></i>
                </b-button>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Hidden File Input -->
      <input
          ref="fileInput"
          type="file"
          :accept="acceptedTypes"
          :multiple="allowMultiple"
          @change="handleFileSelect"
          style="display: none"
      >

      <!-- Error Messages -->
      <b-alert v-if="errorMessage" variant="danger" show class="mt-3">
        {{ errorMessage }}
      </b-alert>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'ModalUploadDocumentos',
  props: {
    modalId: {
      type: String,
      default: 'modal-upload-documentos'
    },
    title: {
      type: String,
      default: 'Upload de Arquivos'
    },
    acceptedTypes: {
      type: String,
      default: '*'
    },
    maxFileSize: {
      type: Number,
      default: 5242880 // 5MB
    },
    maxFiles: {
      type: Number,
      default: 1
    },
    allowMultiple: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedFiles: [],
      isDragOver: false,
      errorMessage: ''
    }
  },
  methods: {
    handleDrop(e) {
      e.preventDefault()
      this.isDragOver = false
      const files = Array.from(e.dataTransfer.files)
      this.processFiles(files)
    },
    handleFileSelect(e) {
      const files = Array.from(e.target.files)
      this.processFiles(files)
    },
    handleDropzoneClick() {
      if(this.selectedFiles.length === 0) this.$refs.fileInput.click();
    },
    processFiles(files) {
      this.errorMessage = ''

      // Check file count
      if (!this.allowMultiple && files.length > 1) {
        this.errorMessage = 'Apenas um arquivo é permitido'
        return
      }

      if (this.selectedFiles.length + files.length > this.maxFiles) {
        this.errorMessage = `Máximo de ${this.maxFiles} arquivo(s) permitido(s)`
        return
      }

      const validFiles = []

      for (const file of files) {
        // Check file size
        if (file.size > this.maxFileSize) {
          this.errorMessage = `Arquivo "${file.name}" excede o tamanho máximo de ${this.formatFileSize(this.maxFileSize)}`
          continue
        }

        // Check file type
        if (this.acceptedTypes !== '*' && !this.isFileTypeAllowed(file)) {
          this.errorMessage = `Tipo de arquivo "${file.name}" não permitido`
          continue
        }

        validFiles.push(file)
      }

      if (this.allowMultiple) {
        this.selectedFiles.push(...validFiles)
      } else {
        this.selectedFiles = validFiles
      }
    },
    isFileTypeAllowed(file) {
      const types = this.acceptedTypes.split(',').map(type => type.trim())
      return types.some(type => {
        if (type.startsWith('.')) {
          return file.name.toLowerCase().endsWith(type.toLowerCase())
        }
        return file.type.match(type.replace('*', '.*'))
      })
    },
    removeFile(index) {
      this.selectedFiles.splice(index, 1)
      this.errorMessage = ''
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    handleConfirm(e) {
      if (this.selectedFiles.length === 0) {
        e.preventDefault()
        this.errorMessage = 'Selecione pelo menos um arquivo'
        return
      }
      this.$emit('files-selected', {
        action: 'upload',
        files: this.selectedFiles
      })
    },
    handleCancel() {
      this.resetModal()
      this.$emit('files-selected', {
        action: 'cancel',
        files: []
      })
    },
    resetModal() {
      this.selectedFiles = []
      this.errorMessage = ''
      this.isDragOver = false
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    }
  }
}
</script>

<style scoped>
::v-deep .modal-content{
  border: none;
  border-radius: 1rem;
  box-shadow: 4px 6px 20px -3px #00000063;
}
::v-deep .modal-header{
  border: none;
  padding: 1rem 1.5rem;
}
::v-deep .modal-header h5{
  font-weight: 600;
}

::v-deep .modal-header .close {
  font-size: 25px;
  justify-self: center;
  align-self: center;
  margin-right: 6px;
  border-radius: 100%;
  width: 37px;
  height: 37px;
  display: flex;
  justify-content: center;
  line-height: 0px;
  margin-top: -3px;
}
::v-deep .modal-header .close:hover{
  background-color: rgba(3, 7, 18, 0.05);
}

::v-deep .modal-body{
  padding: 1rem 1.5rem;
}

::v-deep .modal-footer{
  border: none;
  padding: 1rem 1.5rem;
}

::v-deep .modal-footer .btn-primary {
  width: fit-content;
  padding: 6px 20px;
  height: 49px;
  background: var(--primary-color) 0% 0% no-repeat padding-box;
  border: none;
  border-radius: 40px;
  font: normal normal 300 18px/20px 'Kanit', sans-serif;
  letter-spacing: 0px;
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .modal-footer .btn-primary:hover {
  outline: 2px solid #5cb3ff90;
}
::v-deep .modal-footer .btn-primary.disabled{
  background-color: transparent !important;
  color: rgba(156, 163, 175, 0.38);
}

::v-deep .modal-footer .btn-primary.disabled:hover {
  outline:none;
}

::v-deep .modal-footer .btn-secondary {
  display: inline-block !important;
  background: none;
  color: rgba(3, 7, 18, 0.6);
  border: none;
  border-radius: 40px;
  padding: 6px 20px;
  height: 49px;
  font: normal normal 300 18px/20px 'Kanit', sans-serif;
  letter-spacing: 0px;
}
::v-deep .modal-footer .btn-secondary:hover {
  background-color: rgba(3, 7, 18, 0.05);
}

.drop-zone {
  border: 2px dashed rgb(209 213 219 / 1);
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  align-items: center;
}
.drop-zone.files-active {
  align-items: start;
}


.drop-zone.drag-over {
  border-color: #007bff;
  background-color: #f8f9fa;
}

.drop-zone-content {
  color: rgba(3, 7, 18, 0.6);
  height: 100%;
  width: 100%;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
}

.drop-zone-content i.icon-upload {
  border-radius: 100%;
  background-color: rgb(243, 244, 246);
  width: 80px;
  height: 80px;
  justify-content: center;
  align-content: center;
  color: rgb(156, 163, 175);
}

.file-list {
  width: 100%;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  border: 1px solid #dee2e6;
  border-radius: calc(0.5rem * 1.5);
  margin-bottom: 8px;
  background-color: rgb(249 250 251 / 1);
}

.file-info {
  display: flex;
  align-items: center;
  flex: 1;
  color: #737373;
}
.file-info i {
  font-size: 13px;
}
.file-info span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
}
.remove-file-btn{
  background: none;
  border: none;
  color: #737373;
  padding: 0;
  margin: 0;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 100%;
}
.remove-file-btn:hover{
  background-color: rgba(3, 7, 18, 0.11);
  color: #737373;
}


</style>
